import {configureStore, Middleware, Store} from '@reduxjs/toolkit';
import {setupListeners} from '@reduxjs/toolkit/query';
import axios from 'axios';

import {fileStorageApi, metadaApi, omneticApi, promptBridgeApi, publicApi} from '@dms/api/base';
import {cachedApi, chatAgentApi} from '@dms/api/core';
import {DEFAULT_LANG, createCallApi, AppDispatch, ThunkExtra} from '@dms/teas';

import {reducers} from './reducers';

interface AppStore extends Store {
  dispatch: AppDispatch;
}

const initStore = () => {
  const context: Partial<ThunkExtra> = {};

  const setAcceptLanguageMiddleware: Middleware = (store) => (next) => (action) => {
    // try catch is needed because of store.getState() was rarely called during middleware execution
    // before the store was fully initialized, causing Redux to throw error #3
    try {
      const state = store.getState();
      axios.defaults.headers.common['Accept-Language'] = state?.lang?.value || DEFAULT_LANG;
    } catch {
      axios.defaults.headers.common['Accept-Language'] = DEFAULT_LANG;
    }

    return next(action);
  };

  const store: AppStore = configureStore({
    reducer: reducers,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        immutableCheck: false,
        serializableCheck: {
          ignoredPaths: [`${omneticApi.reducerPath}.queries.subscribeToNotifications.originalArgs`],
        },
        thunk: {
          extraArgument: context,
        },
      })
        .concat(setAcceptLanguageMiddleware)
        .concat(
          omneticApi.middleware,
          metadaApi.middleware,
          fileStorageApi.middleware,
          publicApi.middleware,
          cachedApi.middleware,
          promptBridgeApi.middleware,
          chatAgentApi.middleware
        ),
  });

  context.callApi = createCallApi(store);

  return store;
};

export const store = initStore();

setupListeners(store.dispatch);
